import { Link, useParams } from "wouter";
import { overviewRegistry } from "../overview_registry.js";
import { useListItemVersions, ItemSlug, DateDisplay } from "../components.js";
import type { ItemVersion } from "scantling-common";

function PhaseSection({
  phase,
  items,
  loading,
  error,
}: {
  phase: string;
  items: ItemVersion[];
  loading: boolean;
  error: string | null;
}) {
  const { orgSlug } = useParams<{ orgSlug: string }>();

  if (loading) {
    return (
      <div className="mb-4">
        <div className="mb-2 flex items-center justify-end gap-2">
          <span className="text-xs text-muted-foreground capitalize">
            {phase}
          </span>
        </div>
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4">
        <div className="mb-2 flex items-center justify-end gap-2">
          <span className="text-xs text-muted-foreground capitalize">
            {phase}
          </span>
        </div>
        <div className="text-sm text-destructive">{error}</div>
      </div>
    );
  }

  const top10 = items.slice(0, 10);

  return (
    <div className="mb-4 last:mb-0">
      <div className="mb-2 flex items-center justify-end gap-1">
        <span className="text-xs text-muted-foreground capitalize">
          {phase}
        </span>
        <span className="text-xs text-muted-foreground">·</span>
        <Link
          href={`/o/${orgSlug}/${phase}`}
          className="text-xs text-muted-foreground hover:underline"
        >
          View all ({items.length})
        </Link>
      </div>
      {top10.length === 0 ? (
        <div className="text-sm text-muted-foreground">No items</div>
      ) : (
        <ul className="space-y-1">
          {top10.map(item => (
            <li
              key={`${item.slug}-${item.version}`}
              className="flex items-center justify-between text-sm"
            >
              <div className="flex items-center gap-2">
                <ItemSlug slug={item.slug} version={item.version} item={item} />
                {item.niceName && (
                  <span className="text-muted-foreground">{item.niceName}</span>
                )}
              </div>
              <DateDisplay
                value={item.createdAt}
                className="text-xs text-muted-foreground"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WorkflowOverviewContent() {
  const inbox = useListItemVersions(
    "phase:inbox version:latest",
    undefined,
    10,
  );
  const editing = useListItemVersions(
    "phase:editing version:latest",
    undefined,
    10,
  );
  const signoff = useListItemVersions(
    "phase:signoff version:latest",
    undefined,
    10,
  );

  return (
    <div className="divide-y">
      <PhaseSection
        phase="inbox"
        items={inbox.data?.entries ?? []}
        loading={inbox.isLoading}
        error={inbox.error ?? null}
      />
      <PhaseSection
        phase="editing"
        items={editing.data?.entries ?? []}
        loading={editing.isLoading}
        error={editing.error ?? null}
      />
      <PhaseSection
        phase="signoff"
        items={signoff.data?.entries ?? []}
        loading={signoff.isLoading}
        error={signoff.error ?? null}
      />
    </div>
  );
}

overviewRegistry.register({
  type: "workflow",
  order: 1,
  title: "Items",
  content: WorkflowOverviewContent,
});
